import React, { useEffect, useState, useCallback } from "react";
import GlobalStyles from "./GlobalStyles";
import Scenario1 from "./scenarios/Scenario1";
import Scenario2 from "./scenarios/Scenario2";
import Scenario3 from "./scenarios/Scenario3";
import { LoadingState, ErrorState } from "./ui/LoadingStates";


export const calculateLPH = (
  growthStageData,
  plantingDate,
  hoursPerDay = 6
) => {
  if (!growthStageData || !growthStageData.growth_stages || !plantingDate) {
    return 0;
  }

  try {

    const monthlyDistribution = calculateMonthlyDistribution(
      growthStageData.growth_stages,
      plantingDate
    );


    const maxMonthlyWaterLiters = Math.max(
      ...monthlyDistribution.map((month) => month.value)
    );

    if (maxMonthlyWaterLiters <= 0) {
      return 0;
    }


    const lph = maxMonthlyWaterLiters / 30 / hoursPerDay;
    return lph;
  } catch (error) {
    console.error("Error calculating LPH:", error);
    return 0;
  }
};

export const calculateMultiCropLPH = (combinedData, hoursPerDay = 6) => {
  if (!combinedData || !combinedData.crops || combinedData.crops.length === 0) {
    return 0;
  }

  try {

    const monthlyTotalWater = Array(12).fill(0);


    combinedData.crops.forEach((cropResult) => {
      if (
        !cropResult.growth_stage_calculations ||
        !cropResult.growth_stage_calculations.growth_stages ||
        !cropResult.crop_input.planting_date
      ) {
        return;
      }


      const cropMonthlyWater = calculateCropMonthlyDistribution(
        cropResult.growth_stage_calculations.growth_stages,
        cropResult.crop_input.planting_date
      );


      cropMonthlyWater.forEach((monthWater, monthIndex) => {
        monthlyTotalWater[monthIndex] += monthWater;
      });
    });


    const maxMonthlyWaterLiters = Math.max(...monthlyTotalWater);

    if (maxMonthlyWaterLiters <= 0) {
      return 0;
    }


    const lph = maxMonthlyWaterLiters / 30 / hoursPerDay;

    return lph;
  } catch (error) {
    console.error("Error calculating multi-crop LPH:", error);
    return 0;
  }
};


const calculateCropMonthlyDistribution = (stagesData, plantingDateStr) => {
  const monthlyWater = Array(12).fill(0);

  if (!stagesData || !plantingDateStr) {
    return monthlyWater;
  }

  try {

    const plantingDate = new Date(plantingDateStr);


    let totalDays = 0;
    let totalWaterLiters = 0;

    Object.entries(stagesData).forEach(([stage, data]) => {
      if (data) {
        totalDays += data.days || 0;
        totalWaterLiters += data.total_water_requirement_liters || 0;
      }
    });

    if (totalDays === 0 || totalWaterLiters === 0) {
      return monthlyWater;
    }


    const waterPerDay = totalWaterLiters / totalDays;


    let currentDate = new Date(plantingDate);


    for (let day = 0; day < totalDays; day++) {
      const month = currentDate.getMonth();
      monthlyWater[month] += waterPerDay;


      currentDate.setDate(currentDate.getDate() + 1);
    }
  } catch (error) {
    console.error("Error calculating crop monthly distribution:", error);
  }

  return monthlyWater;
};


const calculateMonthlyDistribution = (stagesData, plantingDateStr) => {
  const MONTHS = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  if (!stagesData || !plantingDateStr) {
    return MONTHS.map((month, index) => ({
      name: month,
      value: 0,
      month: index,
    }));
  }


  const monthlyWater = Array(12).fill(0);

  try {

    const plantingDate = new Date(plantingDateStr);


    let totalDays = 0;
    let totalWaterLiters = 0;

    Object.entries(stagesData).forEach(([stage, data]) => {
      if (data) {
        totalDays += data.days || 0;
        totalWaterLiters += data.total_water_requirement_liters || 0;
      }
    });

    if (totalDays === 0 || totalWaterLiters === 0) {
      return MONTHS.map((month, index) => ({
        name: month,
        value: 0,
        month: index,
      }));
    }


    const waterPerDay = totalWaterLiters / totalDays;


    let currentDate = new Date(plantingDate);


    for (let day = 0; day < totalDays; day++) {
      const month = currentDate.getMonth();
      monthlyWater[month] += waterPerDay;


      currentDate.setDate(currentDate.getDate() + 1);
    }
  } catch (error) {
    console.error("Error calculating monthly distribution:", error);
  }


  return MONTHS.map((month, index) => ({
    name: month,
    value: monthlyWater[index],
    month: index,
  }));
};

const PUBLIC_BASE_PATH = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
const API_URL = (
  process.env.REACT_APP_API_URL || `${PUBLIC_BASE_PATH}/backend`
).replace(/\/$/, "");

const IrrigationScenarioSelector = ({
  onScenarioDataChange,
  onIrrigationResults,
  cropData,
  growthStageData,
  plantingDate,
  areaInAcres,
  combinedData,
}) => {
  const [activeScenario, setActiveScenario] = useState("standard");
  const [standardScenarioData, setStandardScenarioData] = useState(null);
  const [drippersScenarioData, setDrippersScenarioData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);


  const [allPipeSizes, setAllPipeSizes] = useState([]);
  const [filteredPipeSizes, setFilteredPipeSizes] = useState([]);
  const [pipeClasses, setPipeClasses] = useState([]);
  const [classesByMaterial, setClassesByMaterial] = useState({});
  const [selectedClass, setSelectedClass] = useState("");
  const [availableSizesForClass, setAvailableSizesForClass] = useState({});

  const [lphValue, setLphValue] = useState(0);
  const [maxMonthlyWaterLiters, setMaxMonthlyWaterLiters] = useState(0);

  const getAvailableSizesForClass = useCallback(async (classType) => {
    if (!classType) return [];

    try {
      const response = await fetch(
        `${API_URL}/api/v1/pipe/available-sizes/${classType}`
      );

      if (response.ok) {
        const data = await response.json();
        return data.available_sizes || [];
      }
      return [];
    } catch (error) {
      console.error(`Error fetching sizes for class ${classType}:`, error);
      return [];
    }
  }, []);



  useEffect(() => {

    if (activeScenario === "scenario3") {
      return;
    }

    const updateFilteredSizes = async () => {
      if (!selectedClass) {
        setFilteredPipeSizes(allPipeSizes);
        return;
      }


      if (availableSizesForClass[selectedClass]) {
        setFilteredPipeSizes(availableSizesForClass[selectedClass]);
        return;
      }

      setLoading(true);
      try {
        const sizes = await getAvailableSizesForClass(selectedClass);


        setAvailableSizesForClass((prev) => ({
          ...prev,
          [selectedClass]: sizes,
        }));

        setFilteredPipeSizes(sizes);
      } catch (error) {
        console.error("Error updating filtered sizes:", error);
        setFilteredPipeSizes(allPipeSizes);
      } finally {
        setLoading(false);
      }
    };

    updateFilteredSizes();
  }, [
    activeScenario,
    selectedClass,
    allPipeSizes,
    availableSizesForClass,
    getAvailableSizesForClass,
  ]);


  useEffect(() => {

    if (combinedData && combinedData.crops && combinedData.crops.length > 0) {
      const calculatedLPH = calculateMultiCropLPH(combinedData);
      const monthlyTotalWater = Array(12).fill(0);

      combinedData.crops.forEach((cropResult) => {
        if (
          cropResult.growth_stage_calculations &&
          cropResult.growth_stage_calculations.growth_stages &&
          cropResult.crop_input.planting_date
        ) {
          const cropMonthlyWater = calculateCropMonthlyDistribution(
            cropResult.growth_stage_calculations.growth_stages,
            cropResult.crop_input.planting_date
          );

          cropMonthlyWater.forEach((monthWater, monthIndex) => {
            monthlyTotalWater[monthIndex] += monthWater;
          });
        }
      });

      const maxMonthlyWater = Math.max(...monthlyTotalWater);

      setLphValue(calculatedLPH);
      setMaxMonthlyWaterLiters(maxMonthlyWater);
    } else if (
      growthStageData &&
      growthStageData.growth_stages &&
      plantingDate
    ) {
      const monthlyDistribution = calculateCropMonthlyDistribution(
        growthStageData.growth_stages,
        plantingDate
      );

      const maxMonthlyWater = Math.max(...monthlyDistribution);
      setMaxMonthlyWaterLiters(maxMonthlyWater);

      const calculatedLPH = maxMonthlyWater / 30 / 6;
      setLphValue(calculatedLPH);
    } else {

      setLphValue(0);
      setMaxMonthlyWaterLiters(0);
    }
  }, [growthStageData, plantingDate, combinedData]);


  useEffect(() => {
    const fetchPipeData = async () => {
      setLoading(true);
      try {

        const sizesResponse = await fetch(
          `${API_URL}/api/v1/pipe/available_sizes`
        );
        if (!sizesResponse.ok) {
          throw new Error(
            `Failed to fetch pipe sizes: ${sizesResponse.statusText}`
          );
        }
        const sizesData = await sizesResponse.json();
        const sizes = sizesData.sizes || [];
        setAllPipeSizes(sizes);
        setFilteredPipeSizes(sizes);


        const classesResponse = await fetch(
          `${API_URL}/api/v1/pipe/available_classes`
        );
        if (!classesResponse.ok) {
          throw new Error(
            `Failed to fetch pipe classes: ${classesResponse.statusText}`
          );
        }
        const classesData = await classesResponse.json();


        if (classesData.classes_by_material) {

          setClassesByMaterial(classesData.classes_by_material);
          setPipeClasses(classesData.all_classes || classesData.classes || []);
        } else {

          setPipeClasses(classesData.classes || []);


          const groupedClasses = {
            steel:
              classesData.classes?.filter((c) =>
                ["CL6", "CL9", "CL12", "CL15"].includes(c)
              ) || [],
            gi:
              classesData.classes?.filter((c) =>
                ["EL", "L2", "M", "H"].includes(c)
              ) || [],
            hdpe: classesData.classes?.filter((c) => c.startsWith("PN-")) || [],
          };
          setClassesByMaterial(groupedClasses);
        }
      } catch (err) {
        console.error("Error fetching pipe data:", err);
        setError("Failed to load pipe options. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchPipeData();
  }, []);

  const handleScenarioCalculationResults = (
    scenarioType,
    calculationResults,
    powerCalcs
  ) => {
    const irrigationResultsData = {
      scenarioType,
      calculationResults,
      powerCalcs,
      timestamp: new Date().toISOString(),


      lph: scenarioType === "scenario3" && calculationResults ? calculationResults : lphValue,
      maxMonthlyWaterLiters,
    };


    if (onIrrigationResults) {
      onIrrigationResults(scenarioType, irrigationResultsData);
    }
  };

  const handleScenarioChange = (scenarioType) => {
    setActiveScenario(scenarioType);


    let scenarioData = null;

    if (scenarioType === "standard") {
      scenarioData = standardScenarioData;
    } else if (scenarioType === "drippers") {
      scenarioData = drippersScenarioData;
    }

    if (onScenarioDataChange && scenarioData) {
      onScenarioDataChange(scenarioType, scenarioData);
    }
  };


  const handleStandardScenarioData = (data) => {

    if (data.classChangeRequest) {
      setSelectedClass(data.classChangeRequest);
      return;
    }

    const enhancedData = {
      ...data,
      selectedClass,
      availableSizesForClass: availableSizesForClass[selectedClass] || [],
      filteredPipeSizes,
    };

    const isDataDifferent =
      JSON.stringify(standardScenarioData) !== JSON.stringify(enhancedData);

    if (isDataDifferent) {
      setStandardScenarioData(enhancedData);

      if (activeScenario === "standard" && onScenarioDataChange) {
        onScenarioDataChange("standard", enhancedData);
      }
    }
  };

  const handleDrippersScenarioData = (data) => {
    const enhancedData = {
      ...data,
      selectedClass,
      availableSizesForClass: availableSizesForClass[selectedClass] || [],
      filteredPipeSizes,
    };

    setDrippersScenarioData(enhancedData);
    if (activeScenario === "drippers" && onScenarioDataChange) {
      onScenarioDataChange("drippers", enhancedData);
    }
  };


  const tabStyle = {
    padding: "12px 20px",
    cursor: "pointer",
    borderBottom: "2px solid transparent",
    transition: "all 0.3s ease",
    fontWeight: "500",
    color: "#333",
  };

  const activeTabStyle = {
    ...tabStyle,
    borderBottom: "2px solid #2196F3",
    color: "#2196F3",
    fontWeight: "bold",
  };

  const tabContainerStyle = {
    display: "flex",
    borderBottom: "1px solid #e0e0e0",
    marginBottom: "10px",
  };

  return (
    <div className="section-container iwmi-form-section">
      {loading && (
        <LoadingState
          message="Loading pipe options..."
          description="Please wait while we fetch the latest pipe specifications"
          size="sm"
        />
      )}
      {error && (
        <ErrorState
          title="Failed to load pipe options"
          message={error}
          onRetry={() => window.location.reload()}
        />
      )}

      <div className="tab-container">
        <div className="tab-list">
          <button
            className={`tab-button ${activeScenario === "standard" ? "active" : ""}`}
            onClick={() => handleScenarioChange("standard")}
          >
            💧 Scenario 1: Flood Irrigation System
          </button>
          <button
            className={`tab-button ${activeScenario === "drippers" ? "active" : ""}`}
            onClick={() => handleScenarioChange("drippers")}
          >
            🚰 Scenario 2: Pipe Water Course
          </button>
          <button
            className={`tab-button ${activeScenario === "scenario3" ? "active" : ""}`}
            onClick={() => handleScenarioChange("scenario3")}
          >
            🌾 Scenario 3: (HEIS)
          </button>
        </div>
      </div>

      <div className="tab-content">
        {activeScenario === "standard" && (
          <Scenario1
            lph={lphValue}
            maxMonthlyWaterLiters={maxMonthlyWaterLiters}
            pipeSizes={filteredPipeSizes}
            pipeClasses={pipeClasses}
            selectedClass={selectedClass}
            classesByMaterial={classesByMaterial}
            allPipeSizes={allPipeSizes}
            onDataChange={handleStandardScenarioData}
            onCalculationResults={(calculationResults, powerCalcs) =>
              handleScenarioCalculationResults(
                "standard",
                calculationResults,
                powerCalcs
              )
            }
          />
        )}

        {activeScenario === "drippers" && (
          <Scenario2
            lph={lphValue}
            maxMonthlyWaterLiters={maxMonthlyWaterLiters}
            pipeSizes={filteredPipeSizes}
            pipeClasses={pipeClasses}
            selectedClass={selectedClass}
            classesByMaterial={classesByMaterial}
            allPipeSizes={allPipeSizes}
            onDataChange={handleDrippersScenarioData}
            onCalculationResults={(calculationResults, powerCalcs) =>
              handleScenarioCalculationResults(
                "drippers",
                calculationResults,
                powerCalcs
              )
            }
          />
        )}

        {activeScenario === "scenario3" && (
          <Scenario3
            lph={lphValue}
            maxMonthlyWaterLiters={maxMonthlyWaterLiters}
            pipeSizes={filteredPipeSizes}
            pipeClasses={pipeClasses}
            selectedClass={selectedClass}
            classesByMaterial={classesByMaterial}
            allPipeSizes={allPipeSizes}
            onDataChange={(data) => {

              if (onScenarioDataChange) {
                onScenarioDataChange("scenario3", data);
              }
            }}
            onCalculationResults={(calculationResults, powerCalcs) =>
              handleScenarioCalculationResults(
                "scenario3",
                calculationResults,
                powerCalcs
              )
            }
          />
        )}
      </div>
    </div>
  );
};

export default IrrigationScenarioSelector;
